import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import LoadingScreen from "@/components/LoadingScreen";
import { ServicesManagementSection } from "@/components/admin/ServicesManagementSection";
import { UsersManagementSection } from "@/components/admin/UsersManagementSection";
import { FeedbackManagementSection } from "@/components/admin/FeedbackManagementSection";
import { UserReportsManagement } from "@/components/admin/UserReportsManagement";
import { PlatformFinancials } from "@/components/admin/PlatformFinancials";
import { useAdminData } from "@/hooks/useAdminData";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CreditCard, MessageSquare, Scale, Activity } from "lucide-react";

const AdminDashboard = () => {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { 
    users, 
    feedback, 
    services, 
    newsletterSubscriptions,
    loading, 
    servicesLoading, 
    setServices, 
    refreshServices,
    refreshFeedback 
  } = useAdminData(isAdmin);

  // Show loading while checking role
  if (roleLoading) {
    return <LoadingScreen />;
  }

  // Redirect non-admins to homepage
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 space-y-8">
        <div className="mb-8">
          <h1 className="font-poppins font-bold text-4xl mb-4 text-foreground">Admin Dashboard</h1>
          <p className="font-inter text-xl text-muted-foreground">
            Manage users, review feedback, moderate services, and manage newsletter subscriptions
          </p>
        </div>

        {/* Admin Tools */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Tools</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/conversations'}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              All Conversations
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.location.href = '/stripe-test-suite'}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Stripe Test Suite
            </Button>

            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/stripe-diagnostics'}
            >
              <Activity className="h-4 w-4 mr-2" />
              Stripe Diagnostics
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/refunds'}
            >
              <Scale className="h-4 w-4 mr-2" />
              Refunds
            </Button>
            
            <Button
              variant="outline"
              onClick={async () => {
                const proceed = confirm(
                  'This will process all pending transfers to provider accounts that have payouts enabled. Continue?'
                );
                if (!proceed) return;
                try {
                  toast.info('Processing pending transfers...');
                  const { data, error } = await supabase.functions.invoke('process-pending-transfers', { 
                    body: { sweep_all: true } 
                  });
                  if (error) throw error;
                  const summary = data?.summary;
                  toast.success(
                    `Sweep complete: ${summary?.successful || 0} transferred, ${summary?.skipped || 0} skipped, ${summary?.failed || 0} failed`
                  );
                } catch (e: any) {
                  toast.error(e?.message || 'Failed to process transfers');
                }
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Sweep All Now
            </Button>
          </CardContent>
        </Card>

        {/* Platform Financials */}
        <PlatformFinancials />

        <ServicesManagementSection
          services={services}
          servicesLoading={servicesLoading}
          onServicesUpdate={setServices}
          onRefreshServices={refreshServices}
        />

        <UsersManagementSection
          users={users}
          newsletterSubscriptions={newsletterSubscriptions}
          loading={loading}
        />

        <FeedbackManagementSection
          feedback={feedback}
          loading={loading}
          onFeedbackUpdate={refreshFeedback}
        />

        <UserReportsManagement />
      </div>
    </div>
  );
};

export default AdminDashboard;
